import { JSX, createContext, useEffect, useMemo, useState } from 'react';
import { AdminPermissionModel } from '../models/AdminPermissionModel';
import { adminPermissionService } from '../services/admin-permissions';

type CustomerPermissionsContextValue = AdminPermissionModel & {
  isLoading: boolean;
};

type CustomerPermissionsProviderProps = Readonly<{
  children: JSX.Element;
}>;

const EMPTY_PERMISSIONS: AdminPermissionModel = {
  canManageTimesheets: false,
  canManageEmployees: false,
  canImportCards: false,
  canImportOrders: false,
  canImportTimesheets: false,
  canManageCompanies: false,
  canGenerateReports: false,
  canImportEmployees: false,
  canImportBudget: false,
};

export const CustomerPermissionsContext =
  createContext<CustomerPermissionsContextValue>({
    ...EMPTY_PERMISSIONS,
    isLoading: true,
  });

let cachedPermissions: AdminPermissionModel | null = null;
let pendingRequest: Promise<AdminPermissionModel | null> | null = null;

async function loadPermissionsOnce(): Promise<AdminPermissionModel | null> {
  if (cachedPermissions !== null) {
    return cachedPermissions;
  }

  if (pendingRequest !== null) {
    return pendingRequest;
  }

  pendingRequest = adminPermissionService
    .getPrincipalPermissions()
    .then(response => {
      if (response.hasErrors) {
        return null;
      }

      cachedPermissions = response.data;
      return cachedPermissions;
    })
    .catch(() => null)
    .finally(() => {
      pendingRequest = null;
    });

  return pendingRequest;
}

export function CustomerPermissionsProvider({
  children,
}: CustomerPermissionsProviderProps) {
  const [permissions, setPermissions] = useState<AdminPermissionModel>(
    () => cachedPermissions ?? EMPTY_PERMISSIONS,
  );

  const [isLoading, setIsLoading] = useState(() => cachedPermissions === null);

  useEffect(() => {
    if (cachedPermissions !== null) {
      return;
    }

    let isActive = true;

    void loadPermissionsOnce().then(result => {
      if (!isActive) {
        return;
      }

      if (result !== null) {
        setPermissions(result);
      }

      setIsLoading(false);
    });

    return () => {
      isActive = false;
    };
  }, []);

  const contextValue = useMemo<CustomerPermissionsContextValue>(
    () => ({
      ...permissions,
      isLoading,
    }),
    [permissions, isLoading],
  );

  return (
    <CustomerPermissionsContext.Provider value={contextValue}>
      {children}
    </CustomerPermissionsContext.Provider>
  );
}
